import {
  Flex,
  HStack,
  Portal,
  ProgressCircle,
  Select,
  Text,
  createListCollection,
  useSelectContext,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { UseNavigateResult, useParams } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { IndicatorsService } from '@/client';

type ProgressionFilterType = {
  year: string;
  period: string;
  entity_id: string;
};

function getIndicatorsProgressionQueryOptions({ slug }: { slug: string }) {
  return {
    queryKey: ['indicators_progression'],
    queryFn: async () => {
      return await IndicatorsService.readIndicatorsProgression({
        companySlug: slug,
      });
    },
  };
}

const CProgressCircle = ({ progression }: { progression: number | undefined }) => (
  <ProgressCircle.Root
    size="xs"
    value={progression && progression === -1 ? 0 : progression}
    colorPalette={progression && progression === -1 ? 'gray' : 'primary'}
  >
    <ProgressCircle.Circle>
      <ProgressCircle.Track />
      <ProgressCircle.Range strokeLinecap="round" />
    </ProgressCircle.Circle>
  </ProgressCircle.Root>
);

const SelectValueItem = ({ placeholder }: { placeholder: string }) => {
  const select = useSelectContext();
  const items = select.selectedItems;
  const { progression, label } = items[0] || { progression: 0, label: '' };

  return (
    <Select.ValueText placeholder={placeholder}>
      <HStack>
        <CProgressCircle progression={progression} />
        <Text lineClamp="1">{label}</Text>
      </HStack>
    </Select.ValueText>
  );
};

export const ProgressionSelector = ({
  year,
  period = '',
  entity_id = '',
  navigate,
  onlyYear = false,
  strictYear = true,
  onlyEntity = false,
}: {
  year: string;
  period?: string;
  entity_id?: string;
  navigate: UseNavigateResult<''>;
  onlyYear?: boolean;
  strictYear?: boolean;
  onlyEntity?: boolean;
}) => {
  const { slug } = useParams({ strict: false });
  const { t } = useTranslation();

  const { data: indicatorsProgression, isLoading } = useQuery({
    ...getIndicatorsProgressionQueryOptions({ slug }),
  });
  const yearExists = indicatorsProgression?.data.some((el) => el.period === 'year' && el.year.toString() === year);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const lastYear = indicatorsProgression?.data[0]?.year;

    if ((year === '' || (strictYear && !yearExists) || isNaN(Number(year))) && lastYear) {
      navigate({
        search: (prev: Record<string, ProgressionFilterType>) => ({
          ...prev,
          year: lastYear.toString(),
          period: 'year1',
        }),
      });
    }
  }, [isLoading]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: handled manually
  useEffect(() => {
    if (year && period === '' && indicatorsProgression && indicatorsProgression.data.length > 0) {
      // Chercher la première période correspondant à l'année
      const firstValidPeriod = indicatorsProgression?.data.find((el) => el.year.toString() === year);

      if (firstValidPeriod) {
        navigate({
          search: (prev: Record<string, ProgressionFilterType>) => ({
            ...prev,
            period: `${firstValidPeriod.period}${firstValidPeriod.period_number}`,
          }),
        });
      }
    }
  }, [year, period, indicatorsProgression]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const periodData = indicatorsProgression?.data.find(
      (el) =>
        el.year.toString() === year &&
        el.period === period.replace(/\d+/g, '') &&
        el.period_number === Number(period.replace(/\D+/g, ''))
    );

    if (periodData && !periodData.has_company_collect && entity_id === '' && entities.items.length > 0) {
      const firstEntity = entities.items[0];
      navigate({
        search: (prev: Record<string, ProgressionFilterType>) => ({
          ...prev,
          entity_id: firstEntity.value === 'consolidated' ? '' : firstEntity.value,
        }),
      });
    }
  }, [year, period, entity_id, indicatorsProgression]);

  const yearItems = new Map(
    (indicatorsProgression?.data || [])
      .filter((el) => el.period === 'year')
      .map((el) => [
        el.year,
        {
          label: el.year.toString(),
          value: el.year.toString(),
          progression: el.progression,
        },
      ])
  );

  if (!yearExists && year) {
    yearItems.set(Number(year), {
      label: year.toString(),
      value: year.toString(),
      progression: 0,
    });
  }

  const years = createListCollection({
    items: [...yearItems.values()].sort((a, b) => +b.value - +a.value),
  });

  const periods = createListCollection({
    items:
      indicatorsProgression?.data
        .filter((el) => el.year.toString() === year && el.period !== 'year')
        .map((el) => ({
          label: `${el.period.charAt(0).toUpperCase() + el.period.slice(1)} ${el.period_number}`,
          value: `${el.period}${el.period_number}`,
          progression: el.progression,
        })) || [],
  });

  const entities = createListCollection({
    items:
      indicatorsProgression?.data
        .filter((el) => el.year.toString() === year)
        .filter(
          (freq) =>
            freq.period.toString() === period.replace(/\d+/g, '') &&
            freq.period_number.toString() === period.replace(/\D+/g, '')
        )
        .flatMap((freq) => freq.entities || [])
        .map((entity) => ({
          label: entity.name,
          value: entity.id,
          progression: entity.progression,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) || [],
  });

  // at item at start of entities.items
  entities.items.unshift({
    label: t('collection.columnsHeader.consolidated'),
    value: 'consolidated',
    progression: years.items.filter((el) => el.value === year)[0]?.progression,
  });

  return (
    <Flex gap={4}>
      {!onlyYear && (
        <>
          {/* Period Selector */}
          {!onlyEntity && year !== '' && periods.getValues().length !== 0 && (
            <Select.Root
              collection={periods}
              w="180px"
              value={period !== 'year1' ? [period] : []}
              onValueChange={(e) =>
                navigate({
                  search: (prev: Record<string, ProgressionFilterType>) => ({
                    ...prev,
                    period: e.value.length > 0 ? e.value[0] : 'year1',
                    entity_id: '',
                  }),
                })
              }
            >
              <Select.Control>
                <Select.Trigger>
                  <SelectValueItem placeholder={t('collection.header.selectPeriod')} />
                </Select.Trigger>
                <Select.IndicatorGroup>
                  <Select.ClearTrigger />
                  <Select.Indicator />
                </Select.IndicatorGroup>
              </Select.Control>
              <Portal>
                <Select.Positioner>
                  <Select.Content>
                    {periods.items.map((period) => (
                      <Select.Item item={period} key={period.value} justifyContent="flex-start">
                        <CProgressCircle progression={period.progression} />
                        {period.label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Portal>
            </Select.Root>
          )}

          {/* Entity Selector */}
          {year !== '' && entities.getValues().length !== 0 && (
            <Select.Root
              collection={entities}
              w="200px"
              value={entity_id !== '' ? [entity_id] : ['consolidated']}
              onValueChange={(e) =>
                navigate({
                  search: (prev: Record<string, ProgressionFilterType>) => ({
                    ...prev,
                    entity_id: e.value.length > 0 && e.value[0] !== 'consolidated' ? e.value[0] : '',
                  }),
                })
              }
            >
              <Select.Control>
                <Select.Trigger>
                  <SelectValueItem placeholder={t('collection.header.selectEntity')} />
                </Select.Trigger>
                <Select.IndicatorGroup>
                  {entity_id !== '' && <Select.ClearTrigger />}
                  <Select.Indicator />
                </Select.IndicatorGroup>
              </Select.Control>
              <Portal>
                <Select.Positioner>
                  <Select.Content>
                    {entities.items.map((entity) => (
                      <Select.Item item={entity} key={entity.value} justifyContent="flex-start">
                        <CProgressCircle progression={entity.progression} />
                        <Text truncate>{entity.label}</Text>
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Portal>
            </Select.Root>
          )}
        </>
      )}
    </Flex>
  );
};
